import { ParseError } from "../../util/exception";

type Coords = number[];

// A function from [x, y] to [x', y'].
type Transformer = (coords: Coords) => Coords;

interface Path {
    pathElement: SVGPathElement;
    pathData: Segment[];
}

interface Segment {
    type: string;
    relative: boolean;
    // Plus any data stuck in for the segment.
}

const segmentSchemas: { [key: string]: string[] } = {
    m: ["x", "y"],
    z: [],
    l: ["x", "y"],
    h: ["x"],
    v: ["y"],
    c: ["x1", "y1", "x2", "y2", "x", "y"],
    s: ["x2", "y2", "x", "y"],
    q: ["x1", "y1", "x", "y"],
    t: ["x", "y"],
    a: ["rx", "ry", "xRotation", "largeArc", "sweep", "x", "y"],
};

// An object that warps an SVG element.
// Based on Warp.js.
export class Warp {
    private paths: Path[];

    constructor(elem: SVGElement) {
        const pathElements = Array.from(elem.querySelectorAll("path"));
        this.paths = pathElements.map((pathElement: SVGPathElement) => {
            const pathString = pathElement.getAttribute("d") || "";
            const pathData: Segment[] = this.parsePath(pathString);
            return { pathElement, pathData };
        });
    }

    transform(transformer: Transformer): void {
        for (const path of this.paths) {
            path.pathData = this.runTransformer(path.pathData, transformer);
        }
        this.update();
    }

    private encodePath(pathData: Segment[]): string {
        let prevType = "";
        const magnitude = 10 ** 2;
        const callback = (segment: Segment) => {
            const output: string[] = [];
            const outputType = segment.relative ? segment.type : segment.type.toUpperCase();
            let first = prevType !== outputType;
            const schema: string[] = segmentSchemas[segment.type];
            if (first) {
                output.push(outputType);
                prevType = outputType;
            }
            for (const property of schema) {
                const value = (segment as any)[property];
                let outputValue;
                switch (typeof value) {
                    case "boolean":
                        {
                            outputValue = (value as any) | 0;
                        }
                        break;
                    case "number":
                        {
                            outputValue = ((value * magnitude) | 0) / magnitude;
                        }
                        break;
                    default:
                        throw new ParseError("Failed to encode path.");
                }
                if (!first) {
                    output.push(" ");
                }
                output.push(outputValue);
                first = false;
            }
            return output.join("");
        };
        return pathData.map(callback).join("");
    }

    private parsePath(pathString: string): Segment[] {
        const segmentExpr = /([mzlhvcsqta])([^mzlhvcsqta]*)/gi;
        const numberExpr = /-?[0-9]*\.?[0-9]+(?:e[-+]?\d+)?/gi;

        const pathData: { type; relative }[] = [];
        let segmentMatch;
        segmentExpr.lastIndex = 0;

        while ((segmentMatch = segmentExpr.exec(pathString))) {
            const type = segmentMatch[1].toLowerCase();
            const numbers = (segmentMatch[2].match(numberExpr) || []).map(parseFloat);
            const relative = type === segmentMatch[1];
            const schema: string[] = segmentSchemas[type];
            if (numbers.length < schema.length) {
                throw new ParseError(
                    `Path type "${type}" given ${numbers.length} arguments, expected ${schema.length}.`,
                );
            }
            if (schema.length > 0) {
                if (numbers.length % schema.length !== 0) {
                    throw new ParseError(
                        `Path type "${type}" given ${numbers.length} arguments, not divisible by ${schema.length}`,
                    );
                }
                for (let i = 0; i < numbers.length / schema.length; i++) {
                    const segmentData = { type, relative };
                    for (let j = 0; j < schema.length; j++) {
                        (segmentData as any)[schema[j]] = numbers[i * schema.length + j];
                    }
                    pathData.push(segmentData);
                }
            } else {
                pathData.push({ type, relative });
            }
        }

        return pathData;
    }

    private runTransformer(path: any, transformer: Transformer): any {
        const pointGroups = [
            ["x1", "y1"],
            ["x2", "y2"],
            ["x", "y"],
        ];

        const callback = (segment: Segment) => {
            for (let i = 0; i < pointGroups.length; i++) {
                const [x, y]: string[] = pointGroups[i];
                if (x in segment && y in segment) {
                    const oldPoints: Coords = [(segment as any)[x], (segment as any)[y]];
                    const newPoints = transformer(oldPoints);
                    if (newPoints.length < 2) {
                        throw new ParseError("Transformer must return at least 2 points.");
                    }
                    (segment as any)[x] = newPoints[0];
                    (segment as any)[y] = newPoints[1];
                }
            }
            return segment;
        };

        return this.transformPath(path, callback as any);
    }

    private transformPath(path: any, transformer: Transformer): any {
        const newPath: number[] = [];
        for (let i = 0; i < path.length; i++) {
            const segment = JSON.parse(JSON.stringify(path[i]));
            const result: Coords = transformer(segment);
            if (Array.isArray(result)) {
                newPath.push(...result);
            } else if (result) {
                newPath.push(result);
            }
        }
        return newPath;
    }

    private update(): void {
        for (const { pathElement, pathData } of this.paths) {
            const pathString = this.encodePath(pathData);
            pathElement.setAttribute("d", pathString);
        }
    }
}
